import React from "react";
import {
  Modal,
  ModalBody,
  Table,
  TableHeader,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
} from "@windmill/react-ui";

import '../../styles/index.css'

import {
  FiX,
  FiUser,
  FiPhone,
  FiMapPin,
  FiCalendar,
} from "react-icons/fi";


const formatLabel = (text = "") => {
  const labels = {
    min_km: "Minimum Km",
    min_rate: "Minimum Rate",
    parking: "Parking",
    extra_per_km: "Extra / Km",
    night_driver_allowance:
      "Night Driver Allowance",
    extra_km: "Extra Km",
    "5hrs_50km": "5 Hrs / 50 Km",
    "10hrs_100km": "10 Hrs / 100 Km",
  };

  if (labels[text]) return labels[text];

  return text
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};


const getVehicleKeys = (ratesheet) => {
  if (!ratesheet) return [];

  const vehicles = new Set();

  [
    ratesheet.airport_transfer,
    ratesheet.outstation,
    ratesheet.hourly_rental,
    ratesheet.festival_hike,
  ].forEach((section) => {
    if (!section) return;

    Object.values(section).forEach((row) => {
      if (
        row &&
        typeof row === "object"
      ) {
        Object.keys(row).forEach((vehicle) =>
          vehicles.add(vehicle)
        );
      }
    });
  });

  return [...vehicles];
};

const buildRows = (ratesheet) => {
  const rows = [];

  const pushSection = (title, data) => {
    rows.push({
      type: "section",
      title,
    });

    if (
      !data ||
      Object.keys(data).length === 0
    ) {
      rows.push({
        type: "empty",
        label: "No Data Available",
      });

      return;
    }

    Object.entries(data).forEach(
      ([key, value]) => {
        rows.push({
          type: "row",
          label: formatLabel(key),
          values: value || {},
        });
      }
    );
  };

  pushSection(
    "Airport Transfer",
    ratesheet.airport_transfer
  );

  pushSection(
    "Outstation",
    ratesheet.outstation
  );

  pushSection(
    "Hourly Rental",
    ratesheet.hourly_rental
  );

  rows.push({
    type: "section",
    title: "Festival Hike (%)",
  });

  if (
    ratesheet.festival_hike &&
    Object.keys(ratesheet.festival_hike).length
  ) {
    Object.entries(
      ratesheet.festival_hike
    ).forEach(
      ([festival, values]) => {
        rows.push({
          type: "festival",
          label: festival,
          values,
        });
      }
    );
  } else {
    rows.push({
      type: "empty",
      label: "No Festival Available",
    });
  }

  return rows;
};


const InfoCard = ({
  icon,
  title,
  value,
  value2
}) => (
  <div
    className="
      flex
      items-center
      gap-3
      rounded-xl
      border
      border-gray-200
      bg-white
      px-4
      py-3
      shadow-sm
      hover:shadow-md
      transition-all
      duration-200
    "
  >
    <div className="min-w-0 flex-1">

      <p
        className="
          text-sm
          tracking-wider
          text-gray-500
          font-medium
        "
      >
        {title}
      </p>

      <p
        className="
          text-sm
          font-semibold
          text-gray-800
          text-wrap
        "
      >
        {value2 ? `${value2}, ${value || "-"}` : value || "-"}
      </p>

    </div>
  </div>
);


const VendorRateSheetViewModal = ({
  isOpen,
  onClose,
  ratesheet,
}) => {
  if (!ratesheet) return null;

  const vehicleKeys = getVehicleKeys(ratesheet);
  const rows = buildRows(ratesheet);

  return (
    <Modal
      isOpen={isOpen}
      // onClose={onClose}
      className="
        w-full
        max-w-3xl
        rounded-2xl
        overflow-hidden
        p-0
      "
    >
      <div
        className="
    flex
    items-center
    justify-end
    rounded-t-lg
    border-b
    border-gray-200
    px-5
    py-2
    bg-white
  "
      >
        <button
          type="button"
          onClick={onClose}
          className="
      flex
      h-10
      w-10
      items-center
      justify-center
      rounded-full
      text-gray-600
      transition-all
      hover:bg-red-50
      hover:text-red-600
    "
        >
          <FiX size={22} />
        </button>
      </div>


      <ModalBody
        className="bg-white p-5"
        style={{
          maxHeight: "65vh",
          overflow: "auto",
        }}
      >

        <div
          className="
          rounded-t-2xl
          bg-gray-50
          px-5
          py-4
          mb-4
        "
        >

          <div
            className="
            grid
            gap-4
            sm:grid-cols-2
            lg:grid-cols-3
          "
          >

            <InfoCard
              icon={<FiUser size={18} />}
              title="Vendor Name"
              value={ratesheet.vendor_name}
            />

            <InfoCard
              icon={<FiPhone size={18} />}
              title="Phone Number"
              value={ratesheet.phone_number}
            />

            <InfoCard
              icon={<FiMapPin size={18} />}
              title="Place"
              value={ratesheet.state_name}
              value2={ratesheet.city_name}
            />
          </div>

        </div>

        <TableContainer
          className="
            overflow-auto
            rounded-xl
            border
            border-gray-200
            shadow-sm
          "
        // style={{scrollbarWidth:'none'}}
        >

          <Table className="w-full">

            <TableHeader>

              <tr className="sticky top-0 z-30">

                <TableCell
                  className="
                    sticky
                    left-0
                    z-40
                    min-w-[220px]
                    bg-green-700
                    text-white
                    font-bold
                    border-r
                  "
                >
                  Particular
                </TableCell>

                {vehicleKeys.map((vehicle) => (
                  <TableCell
                    key={vehicle}
                    className="
                      min-w-[120px]
                      bg-green-700
                      text-white
                      text-center
                      font-semibold
                      whitespace-nowrap
                    "
                  >
                    {vehicle}
                  </TableCell>
                ))}

              </tr>

            </TableHeader>

            <TableBody>
              {rows.map((row, index) => {
                if (row.type === "section") {
                  return (
                    <TableRow key={`section-${index}`}>
                      <TableCell
                        colSpan={vehicleKeys.length + 1}
                        className="
                          bg-green-100
                          border-y
                          py-2.5
                          font-bold
                          text-green-800
                          uppercase
                          tracking-wide
                          text-xs
                        "
                      >
                        <div className="flex items-center gap-2">

                          <span className="h-2 w-2 rounded-full bg-green-600" />

                          {row.title}

                        </div>
                      </TableCell>
                    </TableRow>
                  );
                }

                if (row.type === "empty") {
                  return (
                    <TableRow key={`empty-${index}`}>
                      <TableCell
                        colSpan={vehicleKeys.length + 1}
                        className="
                          py-8
                          text-center
                          italic
                          text-gray-400
                          bg-gray-50
                        "
                      >
                        {row.label}
                      </TableCell>
                    </TableRow>
                  );
                }
                return (
                  <TableRow
                    key={`row-${index}`}
                    className="
                      odd:bg-white
                      even:bg-gray-50
                      hover:bg-green-50
                      transition-colors
                      duration-150
                    "
                  >

                    <TableCell
                      className="
                        sticky
                        left-0
                        z-20
                        bg-inherit
                        border-r
                        border-gray-200
                        min-w-[220px]
                        py-3
                        font-medium
                        text-gray-700
                      "
                    >
                      {row.label}
                    </TableCell>

                    {vehicleKeys.map((vehicle) => {
                      const value = row.values?.[vehicle];

                      return (
                        <TableCell
                          key={vehicle}
                          className="
                            text-center
                            py-2
                          "
                        >
                          {value !== undefined &&
                            value !== null ? (
                            <span
                              className="
                                inline-flex
                                min-w-[60px]
                                items-center
                                justify-center
                                px-2.5
                                py-1
                                text-sm
                                font-semibold
                                text-black
                              "
                            >
                              {value}
                            </span>
                          ) : (
                            <span className="text-gray-300 text-base">
                              —
                            </span>
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>

          </Table>

        </TableContainer>

      </ModalBody>
    </Modal>
  );
};

export default VendorRateSheetViewModal;
